import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getRequestById,
  createOffer,
  acceptOffer,
  withdrawOffer,
  removeRequest,
} from "../utils/api";

function money(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n.toFixed(2) : "-";
}

export default function RequestDetails({ token, currentUser }) {
  const nav = useNavigate();
  const { id } = useParams();

  const [req, setReq] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // offer form
  const [price, setPrice] = useState("");
  const [msg, setMsg] = useState("");
  const [prefilled, setPrefilled] = useState(false);

  const role = currentUser?.role || "";
  const isAdmin = role === "admin";
  const isSeller = role === "seller" || isAdmin;

  // IMPORTANT: JWT payload uses userId (not id)
  const myId = currentUser?.userId ?? currentUser?.id;

  const isOwner = req && String(req.requester_user_id) === String(myId);

  const myOffer = offers.find(
    (o) => String(o.offerer_user_id) === String(myId),
  );

  const load = useCallback(async () => {
    setErr("");
    setLoading(true);

    if (!token) {
      setReq(null);
      setOffers([]);
      setLoading(false);
      setErr("Please log in to view request details.");
      return;
    }

    try {
      const data = await getRequestById(token, id);
      setReq(data?.request || null);
      setOffers(Array.isArray(data?.offers) ? data.offers : []);
    } catch (e) {
      setErr(e?.message || "Failed to load request.");
      setReq(null);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    load();
  }, [load]);

  // Prefill seller's own offer (only once after load)
  useEffect(() => {
    if (!isSeller) return;

    if (myOffer && !prefilled) {
      setPrice(
        myOffer.offered_price != null ? String(myOffer.offered_price) : "",
      );
      setMsg(myOffer.message || "");
      setPrefilled(true);
    }

    if (!myOffer && prefilled) {
      setPrice("");
      setMsg("");
      setPrefilled(false);
    }
  }, [isSeller, myOffer, prefilled]);

  async function doSubmitOffer() {
    if (!token) return;
    if (!isSeller) return;

    setBusy(true);
    setErr("");
    try {
      await createOffer(token, id, {
        offered_price: price === "" ? null : Number(price),
        message: msg || null,
        item_id: null,
      });
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to submit offer.");
    } finally {
      setBusy(false);
    }
  }

  async function doAcceptOffer(offerId) {
    setBusy(true);
    setErr("");
    try {
      await acceptOffer(token, offerId);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to accept offer.");
    } finally {
      setBusy(false);
    }
  }

  async function doWithdrawOffer(offerId) {
    setBusy(true);
    setErr("");
    try {
      await withdrawOffer(token, offerId);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to withdraw offer.");
    } finally {
      setBusy(false);
    }
  }

  async function doDeleteRequest() {
    if (!token) return;
    if (!window.confirm("Delete this request?")) return;

    setBusy(true);
    setErr("");
    try {
      await removeRequest(token, id);
      nav("/requests");
    } catch (e) {
      setErr(e?.message || "Failed to delete request.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container pagePadBottom">
      <div className="pageHeader">
        <div className="pageHeaderLeft">
          <div className="h2">Request</div>
        </div>
        <div className="pageHeaderRight">
          <Link className="btnSecondary" to="/requests">
            Back
          </Link>
        </div>
      </div>

      {err ? (
        <div className="alert alertDanger">
          <div className="alertTitle">Error</div>
          <div className="alertBody">{err}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="emptyBox">
          <div className="emptyTitle">Loading…</div>
        </div>
      ) : err ? null : !req ? (
        <div className="emptyBox">
          <div className="emptyTitle">Not found</div>
        </div>
      ) : (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="cardTitleRow">
            <div className="cardTitle" style={{ fontSize: 16 }}>
              {req.title}
            </div>
            <span className="badge">
              {String(req.status || "open").toUpperCase()}
            </span>
          </div>

          <div className="muted small" style={{ marginTop: 8 }}>
            Budget: <span className="priceBlue">${money(req.max_budget)}</span>
            {req.created_at ? ` • Posted: ${req.created_at}` : ""}
          </div>

          <div className="divider" />
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
            {req.description || "No description."}
          </div>

          <div className="divider" />

          {/* Owner/Admin actions */}
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            {(isOwner || isAdmin) && (
              <Link className="btnSecondary" to={`/edit-request/${id}`}>
                Edit
              </Link>
            )}
            {(isOwner || isAdmin) && (
              <button
                className="btnDanger"
                onClick={doDeleteRequest}
                disabled={busy}
              >
                Delete
              </button>
            )}
          </div>

          <div className="divider" />

          {/* Seller/Admin: create/update own offer */}
          {isSeller && String(req.status) === "open" ? (
            <>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>
                {myOffer ? "Your Offer" : "Create Offer"}
              </div>

              {myOffer ? (
                <div className="muted small" style={{ marginBottom: 8 }}>
                  Status:{" "}
                  <b>{String(myOffer.status || "pending").toUpperCase()}</b>
                </div>
              ) : null}

              <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                <input
                  className="input"
                  placeholder="Offered price (optional)"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  style={{ maxWidth: 220 }}
                />
                <input
                  className="input"
                  placeholder="Message (optional)"
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  style={{ flex: 1, minWidth: 220 }}
                />

                <button
                  className="btnPrimary"
                  onClick={doSubmitOffer}
                  disabled={busy}
                >
                  {busy ? "Working…" : myOffer ? "Update" : "Send"}
                </button>

                {myOffer && String(myOffer.status) === "pending" ? (
                  <button
                    className="btnSecondary"
                    onClick={() => doWithdrawOffer(myOffer.id)}
                    disabled={busy}
                  >
                    Withdraw
                  </button>
                ) : null}
              </div>

              <div className="divider" />
            </>
          ) : null}

          {/* Offers list (buyer should see all offers on their request) */}
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Offers</div>

          {offers.length === 0 ? (
            <div className="muted small">No offers yet.</div>
          ) : (
            <div className="col" style={{ gap: 10 }}>
              {offers.map((o) => {
                const isOfferOwner = String(o.offerer_user_id) === String(myId);
                return (
                  <div key={o.id} className="card" style={{ padding: 12 }}>
                    <div
                      className="row"
                      style={{ justifyContent: "space-between" }}
                    >
                      <div style={{ fontWeight: 800 }}>
                        {o.offerer_name || "Seller"}
                        {o.offered_price != null
                          ? ` • $${money(o.offered_price)}`
                          : ""}
                        {isOfferOwner ? " • (You)" : ""}
                      </div>
                      <span className="badge">
                        {String(o.status || "pending").toUpperCase()}
                      </span>
                    </div>

                    {o.message ? (
                      <div
                        className="muted small"
                        style={{ marginTop: 6, whiteSpace: "pre-wrap" }}
                      >
                        {o.message}
                      </div>
                    ) : null}

                    <div
                      className="row"
                      style={{ gap: 10, marginTop: 10, flexWrap: "wrap" }}
                    >
                      {/* Buyer(owner) accepts */}
                      {isOwner && String(o.status) === "pending" && (
                        <button
                          className="btnPrimary"
                          onClick={() => doAcceptOffer(o.id)}
                          disabled={busy}
                        >
                          Accept
                        </button>
                      )}

                      {/* Offer owner withdraws */}
                      {isOfferOwner && String(o.status) === "pending" && (
                        <button
                          className="btnSecondary"
                          onClick={() => doWithdrawOffer(o.id)}
                          disabled={busy}
                        >
                          Withdraw
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
